import React, { useEffect, useState } from "react";
import { X, Plus, Minus, Loader2 } from "lucide-react";
import api from "../lib/api";
import { fmtInt, fmtMoneyFull } from "../lib/format";
import { toast } from "sonner";

/**
 * Mobile quick-edit bottom sheet for a single product.
 * - Slides up from the bottom (mobile-first; usable on desktop too).
 * - Per-variant +/- stock buttons (large touch targets) + editable price.
 * - Optimistic UI; auto-saves on blur for prices, immediate API call on +/-.
 */
export default function QuickEditSheet({ product, onClose, onSaved }) {
  const [local, setLocal] = useState(product);
  const [savingId, setSavingId] = useState(null); // `${variantId}:price` or `${variantId}:+|-`
  const [savedFlash, setSavedFlash] = useState(null);

  useEffect(() => { setLocal(product); }, [product?.product_id]);
  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const adjust = async (variant, delta) => {
    const key = `${variant.variant_id}:${delta > 0 ? "+" : "-"}`;
    setSavingId(key);
    const before = Number(variant.stock || 0);
    if (before + delta < 0) {
      setSavingId(null);
      toast.error("Stock can't go below zero");
      return;
    }
    // Optimistic
    setLocal((p) => ({
      ...p,
      variants: p.variants.map((v) =>
        v.variant_id === variant.variant_id ? { ...v, stock: before + delta } : v
      ),
    }));
    try {
      await api.post(`/products/${product.product_id}/variants/${variant.variant_id}/adjust`, {
        delta,
        idempotency_key: `quick_${variant.variant_id}_${Date.now()}`,
      });
      setSavedFlash(key);
      setTimeout(() => setSavedFlash((cur) => (cur === key ? null : cur)), 1200);
      onSaved?.();
    } catch (err) {
      // Roll back
      setLocal((p) => ({
        ...p,
        variants: p.variants.map((v) =>
          v.variant_id === variant.variant_id ? { ...v, stock: before } : v
        ),
      }));
      toast.error(err?.response?.data?.detail || "Couldn't adjust stock");
    } finally {
      setSavingId(null);
    }
  };

  const savePrice = async (variant, newPrice) => {
    const next = Number(newPrice);
    if (!isFinite(next) || next < 0) {
      toast.error("Price must be a non-negative number");
      return;
    }
    const before = Number(variant.price || 0);
    if (next === before) return;
    const key = `${variant.variant_id}:price`;
    setSavingId(key);
    try {
      const variants = local.variants.map((v) =>
        v.variant_id === variant.variant_id ? { ...v, price: next } : v
      );
      await api.put(`/products/${product.product_id}`, { variants });
      setLocal((p) => ({ ...p, variants }));
      setSavedFlash(key);
      setTimeout(() => setSavedFlash((cur) => (cur === key ? null : cur)), 1200);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update price");
    } finally {
      setSavingId(null);
    }
  };

  if (!local) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center sm:justify-center" data-testid="quick-edit-sheet">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} aria-hidden/>
      <div className="relative w-full sm:max-w-md bg-white border-2 border-black shadow-[6px_6px_0_0_#111] sm:shadow-none sm:rounded-none animate-slide-up max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#FFB100] border-b-2 border-black px-4 py-3 flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-widest">Quick edit</div>
            <h2 className="font-heading text-base font-black uppercase truncate">{local.name}</h2>
          </div>
          <button
            onClick={onClose}
            className="brutal-btn brutal-btn-secondary !p-1 !min-w-[36px] !min-h-[36px]"
            aria-label="Close"
            data-testid="quick-edit-close"
          >
            <X size={16}/>
          </button>
        </div>
        <div className="p-4 space-y-3">
          {(local.variants || []).map((v) => {
            const variantLabel = Object.entries(v.attributes || {}).map(([, val]) => val).join(" / ") || "Default";
            const isAdjusting = savingId === `${v.variant_id}:-` || savingId === `${v.variant_id}:+`;
            const flashStock = savedFlash === `${v.variant_id}:-` || savedFlash === `${v.variant_id}:+`;
            const flashPrice = savedFlash === `${v.variant_id}:price`;
            return (
              <div
                key={v.variant_id}
                className="border-2 border-black p-3 space-y-2"
                data-testid={`quick-edit-variant-${v.variant_id}`}
              >
                <div className="flex items-baseline gap-2">
                  <div className="font-mono text-xs uppercase tracking-widest flex-1 truncate">{variantLabel}</div>
                  <div className="font-mono text-[10px] text-[#4A4A4A]">{v.sku}</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {/* Stock control */}
                  <div className="space-y-1">
                    <label className="brutal-label">Stock</label>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => adjust(v, -1)}
                        disabled={isAdjusting || (Number(v.stock || 0) <= 0)}
                        className="brutal-btn brutal-btn-secondary !p-1 !min-w-[44px] !min-h-[44px] disabled:opacity-30"
                        data-testid={`quick-edit-stock-minus-${v.variant_id}`}
                        aria-label="Decrement stock"
                      ><Minus size={14}/></button>
                      <div className={`flex-1 text-center font-mono text-base font-bold py-2 border-2 border-black ${flashStock ? "bg-[#4A7C59] text-white" : "bg-white"}`}>
                        {fmtInt(v.stock || 0)}
                      </div>
                      <button
                        onClick={() => adjust(v, 1)}
                        disabled={isAdjusting}
                        className="brutal-btn !p-1 !min-w-[44px] !min-h-[44px]"
                        data-testid={`quick-edit-stock-plus-${v.variant_id}`}
                        aria-label="Increment stock"
                      ><Plus size={14}/></button>
                    </div>
                  </div>
                  {/* Price */}
                  <div className="space-y-1">
                    <label className="brutal-label">Price</label>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        defaultValue={Number(v.price || 0)}
                        onBlur={(e) => savePrice(v, e.target.value)}
                        className={`brutal-input w-full !text-base !min-h-[44px] ${flashPrice ? "!bg-[#4A7C59] !text-white" : ""}`}
                        data-testid={`quick-edit-price-${v.variant_id}`}
                        aria-label="Price"
                      />
                      {savingId === `${v.variant_id}:price` && <Loader2 size={14} className="animate-spin"/>}
                    </div>
                  </div>
                </div>
                <div className="font-mono text-[10px] text-[#4A4A4A]">
                  Value: {fmtMoneyFull(Number(v.stock || 0) * Number(v.price || 0))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
